import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { API } from "../context/SiteContext";

const CONCERNS = [
  "Anxiety & Stress",
  "Depression & Low mood",
  "Relationship / Couples",
  "Burnout & Career",
  "Trauma & PTSD",
  "Grief & Loss",
  "Life transition",
  "Massage & Spa Therapy",
  "Not sure yet",
];

const AGE_RANGES = ["18–24", "25–34", "35–44", "45–54", "55–64", "65+"];

const INSURANCE_PROVIDERS = [
  "Aetna",
  "Cigna",
  "United / Oxford",
  "Blue Cross Blue Shield",
  "Anthem",
  "Humana",
  "Kaiser",
  "Other",
  "Out-of-network / Self-pay",
  "Not sure yet",
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  age_range: "",
  concern: "",
  preferred_contact: "email",
  insurance_provider: "",
  insurance_member_id: "",
  message: "",
};

export default function Enroll() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.concern) {
      toast.error("Please fill in your name, email, phone and concern.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Strip empty optional strings so backend validation is clean
      if (!payload.insurance_provider) delete payload.insurance_provider;
      if (!payload.insurance_member_id) delete payload.insurance_member_id;
      if (!payload.age_range) delete payload.age_range;
      await axios.post(`${API}/enroll`, payload);
      setDone(true);
      setForm(initial);
      toast.success("We've received your enrollment. A clinician will reach out within 1 business day.");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enroll" className="relative py-24 md:py-32 bg-[#F4F1EA]" data-testid="enroll-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="overline text-[#9F5C45] mb-6">05 — New patient enrollment</p>
          <h2 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8">
            Reach out. We&apos;ll meet you where you are.
          </h2>
          <p className="text-[#57534E] text-base md:text-lg leading-relaxed mb-8">
            Share a few details and one of our care coordinators will call or
            email within 1 business day. Everything you share is HIPAA-protected
            and strictly confidential.
          </p>
          <div className="border-t border-[#E2DCD0] pt-8 space-y-4 text-sm text-[#57534E]">
            <div className="flex justify-between"><span>Response time</span><span className="text-[#1C1917]">Within 1 business day</span></div>
            <div className="flex justify-between"><span>First session</span><span className="text-[#1C1917]">50 minutes</span></div>
            <div className="flex justify-between"><span>Insurance</span><span className="text-[#1C1917]">Most major plans</span></div>
            <div className="flex justify-between"><span>Telehealth</span><span className="text-[#1C1917]">Available across CT</span></div>
          </div>
        </div>

        <div className="md:col-span-7 md:col-start-7">
          {done ? (
            <div data-testid="enroll-success" className="border border-[#E2DCD0] bg-[#FDFBF7] p-10 md:p-12">
              <div className="overline text-[#3A5A40] mb-4">Received</div>
              <h3 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4">Thank you — we have you.</h3>
              <p className="text-[#57534E] leading-relaxed mb-6">
                A confirmation has been emailed to you, and a care coordinator
                will reach out within 1 business day to match you with a therapist.
              </p>
              <div className="border-t border-[#E2DCD0] pt-6 mb-8">
                <p className="overline text-[#9F5C45] mb-2">Next step</p>
                <p className="text-[#1C1917] font-serif text-xl mb-3">Complete your intake paperwork.</p>
                <p className="text-[#57534E] text-sm leading-relaxed mb-5">
                  Two short forms — consent for services and privacy practices acknowledgment. Getting these
                  out of the way now saves 10 minutes at your first session, and you&apos;ll get signed PDFs for your records.
                </p>
                <Link
                  to="/intake"
                  data-testid="enroll-to-intake"
                  className="inline-flex items-center gap-2 px-6 py-3 btn-primary text-xs uppercase tracking-[0.22em]"
                >
                  Start intake paperwork →
                </Link>
              </div>
              <button
                onClick={() => setDone(false)}
                data-testid="enroll-again-btn"
                className="inline-flex items-center gap-2 px-6 py-3 btn-ghost text-sm tracking-wide"
              >
                Submit another enrollment
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-[#FDFBF7] border border-[#E2DCD0] p-8 md:p-12 space-y-7" data-testid="enroll-form">
              <div className="grid sm:grid-cols-2 gap-7">
                <Field label="Full name" required>
                  <input data-testid="enroll-input-name" className="lgw-field" placeholder="Jane Doe" value={form.full_name} onChange={update("full_name")} />
                </Field>
                <Field label="Email" required>
                  <input data-testid="enroll-input-email" type="email" className="lgw-field" placeholder="jane@example.com" value={form.email} onChange={update("email")} />
                </Field>
                <Field label="Phone" required>
                  <input data-testid="enroll-input-phone" className="lgw-field" placeholder="(203) 000-0000" value={form.phone} onChange={update("phone")} />
                </Field>
                <Field label="Age range">
                  <Select value={form.age_range} onValueChange={(v) => update("age_range")(v)}>
                    <SelectTrigger data-testid="enroll-select-age" className="lgw-field h-auto rounded-none border-0 border-b border-[#E2DCD0] px-0 focus:ring-0 focus:border-[#3A5A40] shadow-none">
                      <SelectValue placeholder="Prefer not to say" />
                    </SelectTrigger>
                    <SelectContent>
                      {AGE_RANGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="Primary concern" required>
                <Select value={form.concern} onValueChange={(v) => update("concern")(v)}>
                  <SelectTrigger data-testid="enroll-select-concern" className="lgw-field h-auto rounded-none border-0 border-b border-[#E2DCD0] px-0 focus:ring-0 focus:border-[#3A5A40] shadow-none">
                    <SelectValue placeholder="What brings you in?" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONCERNS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid sm:grid-cols-2 gap-7 pt-2 border-t border-[#E2DCD0]/60">
                <Field label="Insurance provider (optional)">
                  <Select value={form.insurance_provider} onValueChange={(v) => update("insurance_provider")(v)}>
                    <SelectTrigger data-testid="enroll-select-insurance" className="lgw-field h-auto rounded-none border-0 border-b border-[#E2DCD0] px-0 focus:ring-0 focus:border-[#3A5A40] shadow-none">
                      <SelectValue placeholder="Skip if unsure" />
                    </SelectTrigger>
                    <SelectContent>
                      {INSURANCE_PROVIDERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Member ID (optional)">
                  <input data-testid="enroll-input-insurance-member" className="lgw-field" placeholder="On the front of your insurance card" value={form.insurance_member_id} onChange={update("insurance_member_id")} />
                </Field>
              </div>

              <Field label="Preferred contact">
                <div className="flex gap-6 pt-2">
                  {["email", "phone", "text"].map((opt) => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="preferred_contact"
                        value={opt}
                        checked={form.preferred_contact === opt}
                        onChange={update("preferred_contact")}
                        data-testid={`enroll-radio-${opt}`}
                        className="accent-[#3A5A40]"
                      />
                      <span className="text-sm text-[#1C1917] capitalize">{opt}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Anything else you'd like us to know?">
                <textarea data-testid="enroll-textarea-message" rows={4} className="lgw-field resize-none" placeholder="Optional — share as much or as little as you'd like." value={form.message} onChange={update("message")} />
              </Field>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-4">
                <p className="text-xs text-[#57534E] max-w-sm leading-relaxed">
                  By submitting, you agree to be contacted about scheduling.
                  Your information is protected under HIPAA — see our{" "}
                  <a href="/privacy" className="underline underline-offset-2 hover:text-[#3A5A40]">privacy notice</a>.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="enroll-submit-button"
                  className="inline-flex items-center gap-2 px-8 py-4 btn-primary text-sm tracking-wide disabled:opacity-60"
                >
                  {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting</>) : (<>Submit enrollment <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} /></>)}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="overline text-[#57534E] mb-2 block">
      {label}{required && <span className="text-[#9F5C45] ml-1">*</span>}
    </span>
    {children}
  </label>
);
